import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Clock, Trophy, Flag, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API } from '../config/api';

const LapByLapView = ({ raceId, driverNumber }) => {
  const [lapData, setLapData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Sort mode for the "Detalle de Vueltas" table. `lap` is chronological
  // order, `time` sorts by lap_time ascending (fastest first), `position`
  // sorts by the rolling standing at each lap ascending (P1 first).
  const [sortMode, setSortMode] = useState('lap');

  useEffect(() => {
    if (driverNumber) {
      loadLaps();
    }
  }, [raceId, driverNumber]);

  const loadLaps = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/races/${raceId}/drivers/${driverNumber}/laps`);
      setLapData(response.data);
    } catch (error) {
      toast.error('Error al cargar vueltas');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(3);
    return mins > 0 ? `${mins}:${secs.padStart(6, '0')}` : `${secs}s`;
  };

  // Chart data preserves lap order (X axis = lap number) regardless of the
  // table sort mode.
  const chartData = useMemo(() => {
    if (!lapData || !lapData.lap_details) return [];
    return lapData.lap_details.map((lap) => {
      const data = {
        lap: lap.lap_number,
        'Tiempo Vuelta': lap.lap_time,
        position: lap.position || null,
      };
      (lap.sector_times || []).forEach((time, idx) => {
        data[`S${idx + 1}`] = time;
      });
      return data;
    });
  }, [lapData]);

  const positionChartData = useMemo(
    () => chartData.filter((d) => d.position != null),
    [chartData]
  );

  // Table rows: apply the current sort mode without mutating the source.
  const tableRows = useMemo(() => {
    if (!lapData || !lapData.lap_details) return [];
    const rows = [...lapData.lap_details];
    if (sortMode === 'time') {
      rows.sort((a, b) => (a.lap_time || Infinity) - (b.lap_time || Infinity));
    } else if (sortMode === 'position') {
      rows.sort((a, b) => (a.position || 999) - (b.position || 999));
    }
    return rows;
  }, [lapData, sortMode]);

  if (loading) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 text-center">
          <div className="w-16 h-16 mx-auto border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400">Cargando datos de vueltas...</p>
        </CardContent>
      </Card>
    );
  }

  if (!lapData || !lapData.lap_details || lapData.lap_details.length === 0) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 text-center">
          <Clock className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No hay datos de vueltas disponibles</p>
        </CardContent>
      </Card>
    );
  }

  const hasPositionData = positionChartData.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-red-950/50 to-neutral-900 border-red-600">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-white flex items-center gap-2">
                <Clock className="w-7 h-7 text-red-500" />
                Análisis Vuelta por Vuelta
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                #{lapData.driver_number} - {lapData.driver_name}
              </CardDescription>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Mejor Vuelta</p>
              <p className="text-3xl font-bold text-red-500">{formatTime(lapData.best_lap)}</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Chart: lap times evolution */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-white">Evolución de Tiempos</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis
                dataKey="lap"
                stroke="#9ca3af"
                label={{ value: 'Vuelta', position: 'insideBottom', offset: -5, fill: '#9ca3af' }}
              />
              <YAxis stroke="#9ca3af" label={{ value: 'Tiempo (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#171717', border: '1px solid #7f1d1d', borderRadius: '8px' }}
                labelStyle={{ color: '#ffffff' }}
              />
              <Legend />
              <Line type="monotone" dataKey="Tiempo Vuelta" stroke="#dc2626" strokeWidth={3} dot={{ fill: '#dc2626', r: 4 }} />
              {(lapData.lap_details[0]?.sector_times || []).map((_, idx) => (
                <Line
                  key={`S${idx + 1}`}
                  type="monotone"
                  dataKey={`S${idx + 1}`}
                  stroke={`hsl(${idx * 60}, 70%, 50%)`}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Lap Details Table */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-white">Detalle de Vueltas</CardTitle>
            <div className="flex items-center gap-2" data-testid="lap-table-sort-toggle">
              <ArrowUpDown className="w-3.5 h-3.5 text-gray-500" />
              <div className="inline-flex rounded-md border border-neutral-700 bg-neutral-900/60 p-0.5">
                <button
                  onClick={() => setSortMode('lap')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    sortMode === 'lap' ? 'bg-neutral-700 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid="lap-table-sort-lap"
                >
                  Vuelta
                </button>
                <button
                  onClick={() => setSortMode('time')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    sortMode === 'time' ? 'bg-neutral-700 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid="lap-table-sort-time"
                >
                  Tiempo
                </button>
                <button
                  onClick={() => setSortMode('position')}
                  disabled={!hasPositionData}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    sortMode === 'position'
                      ? 'bg-neutral-700 text-white'
                      : 'text-gray-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed'
                  }`}
                  data-testid="lap-table-sort-position"
                  title={hasPositionData ? 'Ordenar por posición' : 'Sin datos de posición para esta sesión'}
                >
                  Posición
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-4 text-gray-300 font-semibold">Vuelta</th>
                  {hasPositionData && (
                    <th className="text-center py-3 px-4 text-gray-300 font-semibold">Pos</th>
                  )}
                  {(lapData.lap_details[0]?.sector_times || []).map((_, idx) => (
                    <th key={`S${idx + 1}`} className="text-center py-3 px-4 text-gray-300 font-semibold">
                      S{idx + 1}
                    </th>
                  ))}
                  <th className="text-center py-3 px-4 text-gray-300 font-semibold">Tiempo</th>
                  <th className="text-center py-3 px-4 text-gray-300 font-semibold">vs Mejor</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((lap) => {
                  const isBestLap = Math.abs(lap.lap_time - lapData.best_lap) < 0.001;
                  const delta = lap.lap_time - lapData.best_lap;
                  const pos = lap.position;
                  const isPodium = pos && pos <= 3;
                  return (
                    <tr
                      key={lap.lap_number}
                      className={`border-b border-neutral-800 hover:bg-red-950/20 ${isBestLap ? 'bg-red-950/30' : ''}`}
                      data-testid={`lap-row-${lap.lap_number}`}
                    >
                      <td className="py-3 px-4 font-medium text-white">
                        <div className="flex items-center gap-2">
                          {lap.lap_number}
                          {isBestLap && <Trophy className="w-4 h-4 text-red-500" />}
                        </div>
                      </td>
                      {hasPositionData && (
                        <td className="text-center py-3 px-4" data-testid={`lap-position-${lap.lap_number}`}>
                          {pos ? (
                            <span
                              className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-xs font-bold ${
                                pos === 1
                                  ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                                  : isPodium
                                    ? 'bg-neutral-700/40 text-gray-200 border border-neutral-600'
                                    : 'text-gray-300'
                              }`}
                            >
                              P{pos}
                            </span>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </td>
                      )}
                      {(lap.sector_times || []).map((time, idx) => (
                        <td key={idx} className="text-center py-3 px-4 text-gray-300 font-mono">
                          {time.toFixed(3)}s
                        </td>
                      ))}
                      <td className="text-center py-3 px-4 text-white font-mono font-semibold">
                        {formatTime(lap.lap_time)}
                      </td>
                      <td className="text-center py-3 px-4">
                        {isBestLap ? (
                          <Badge className="bg-red-600 text-white">
                            <Flag className="w-3 h-3 mr-1" />
                            MEJOR
                          </Badge>
                        ) : (
                          <span className="font-mono text-sm text-red-400">
                            +{delta.toFixed(3)}s
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LapByLapView;
